'use client';

import React from 'react';
import Link from 'next/link';
import { Shield, ArrowLeft } from 'lucide-react';

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Back to Ertzyx
          </Link>
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-blue-700" />
            <span className="font-bold text-slate-800">Ertzyx</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-3">Cookie Policy</h1>
          <p className="text-slate-500 text-sm">
            Last updated: 25 March 2026 · Effective: 25 March 2026
          </p>
          <p className="text-slate-600 mt-4 leading-relaxed">
            This Cookie Policy explains how Ertzyx (&quot;we&quot;, &quot;us&quot;, or
            &quot;our&quot;) uses cookies and similar tracking technologies on{' '}
            <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
              ledger.ertzyx.com
            </a>
            . By using our Platform, you consent to our use of cookies as described in this policy.
          </p>
        </div>

        <div className="space-y-10 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. What Are Cookies?</h2>
            <p>
              Cookies are small text files placed on your device when you visit a website. They are
              widely used to make websites work efficiently, provide a better user experience, and
              give website owners information about how their site is used. Cookies can be
              &quot;session cookies&quot; (deleted when you close your browser) or &quot;persistent
              cookies&quot; (remain on your device for a set period).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Cookies We Use</h2>
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Essential Cookies</h3>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                      Always Active
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-slate-600 mb-4">
                    These cookies are necessary for the Platform to function and cannot be disabled.
                    They are typically set in response to actions you take, such as logging in or
                    filling in forms.
                  </p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="pb-2 font-medium">Cookie Name</th>
                        <th className="pb-2 font-medium">Purpose</th>
                        <th className="pb-2 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="py-2 font-mono text-slate-700">sb-auth-token</td>
                        <td className="py-2 text-slate-600">Authentication session management</td>
                        <td className="py-2 text-slate-500">Session / 7 days</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono text-slate-700">ertzyx-cookie-consent</td>
                        <td className="py-2 text-slate-600">
                          Stores your cookie consent preference
                        </td>
                        <td className="py-2 text-slate-500">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Analytics Cookies</h3>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                      Consent Required
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-slate-600 mb-4">
                    These cookies help us understand how visitors interact with the Platform by
                    collecting and reporting information anonymously. We only set these cookies with
                    your consent.
                  </p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="pb-2 font-medium">Cookie Name</th>
                        <th className="pb-2 font-medium">Purpose</th>
                        <th className="pb-2 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <tr>
                        <td className="py-2 font-mono text-slate-700">_clck</td>
                        <td className="py-2 text-slate-600">
                          Microsoft Clarity — persists a pseudonymous visitor ID across visits
                        </td>
                        <td className="py-2 text-slate-500">1 year</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-mono text-slate-700">_clsk</td>
                        <td className="py-2 text-slate-600">
                          Microsoft Clarity — connects page views into a single session recording
                        </td>
                        <td className="py-2 text-slate-500">1 day</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Functional Cookies</h3>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                      Preference-Based
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-slate-600">
                    These cookies enable enhanced functionality and personalisation. They may be set
                    by us or by third-party providers whose services we use. Currently, we do not
                    use any functional cookies beyond the essential ones listed above.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Third-Party Cookies</h2>
            <p className="mb-3">Some cookies on our Platform are set by trusted third-party service providers:</p>
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">
                  Authentication and Security Providers
                </h3>
                <p className="text-sm text-slate-600">
                  Our authentication and security infrastructure providers may set cookies required
                  for secure login and session management. These are essential cookies and cannot be
                  disabled without preventing access to the Platform.
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-slate-800 mb-1">Payment Processing Providers</h3>
                <p className="text-sm text-slate-600">
                  If you use paid features, our payment processing provider may set cookies during
                  payment flows for fraud prevention and security purposes. These cookies are only
                  active during payment interactions.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              4. Managing Your Cookie Preferences
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Cookie Consent Banner</h3>
                <p className="text-slate-600">
                  On your first visit to Ertzyx, you will see a cookie consent banner. You can
                  accept all cookies or decline non-essential cookies. You can change your
                  preference at any time by clearing your browser cookies and revisiting the site.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Browser Settings</h3>
                <p className="text-slate-600 mb-2">
                  You can control cookies through your browser settings. Most browsers allow you to:
                </p>
                <ul className="space-y-1 list-disc list-inside text-slate-600 text-sm">
                  <li>View and delete existing cookies</li>
                  <li>Block cookies from specific websites</li>
                  <li>Block all third-party cookies</li>
                  <li>Block all cookies (note: this will break authentication)</li>
                </ul>
                <p className="mt-2 text-sm text-slate-500">
                  Browser-specific instructions:{' '}
                  <a
                    href="https://support.google.com/chrome/answer/95647"
                    className="text-blue-700 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Chrome
                  </a>{' '}
                  ·{' '}
                  <a
                    href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                    className="text-blue-700 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Firefox
                  </a>{' '}
                  ·{' '}
                  <a
                    href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                    className="text-blue-700 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Safari
                  </a>
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Do Not Track</h2>
            <p>
              Some browsers include a &quot;Do Not Track&quot; (DNT) feature. We currently do not
              respond to DNT signals, as there is no industry-standard interpretation. We will
              update this policy if a standard is established.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Changes to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Changes will be communicated via a
              notice on the Platform. Continued use of Ertzyx after changes constitutes acceptance
              of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Contact</h2>
            <p>For questions about our use of cookies:</p>
            <div className="mt-3 bg-white border border-slate-200 rounded-xl p-5">
              <p className="font-semibold text-slate-800">Ertzyx Privacy Team</p>
              <p className="text-slate-600 text-sm mt-1">
                Email:{' '}
                <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                  support@ertzyx.com
                </a>
              </p>
              <p className="text-slate-600 text-sm">
                Platform:{' '}
                <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
                  ledger.ertzyx.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer nav */}
      <div className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap gap-4 text-sm text-slate-500">
          <Link href="/privacy-policy" className="hover:text-slate-800 transition-colors">
            Privacy Policy
          </Link>
          <Link href="/terms-of-service" className="hover:text-slate-800 transition-colors">
            Terms of Service
          </Link>
          <Link href="/data-retention" className="hover:text-slate-800 transition-colors">
            Data Retention
          </Link>
          <Link href="/contact" className="hover:text-slate-800 transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
